import { useCallback, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { toast } from "sonner";

import {
  ADJUDICATE_ANSWER,
  OPEN_ANSWER_DISCUSSION,
  REQUEST_PROBLEM_HELP,
  SEND_DISCUSSION_MESSAGE,
  RESOLVE_DISCUSSION,
} from "@/graphql/mutations/sessions";
import type { Discussion, Exercise } from "@/graphql/types";

/**
 * Mirrors `DiscussionTutor.opening_message(nil)` (app/services/discussion_tutor.rb).
 * The tutor's first turn is fixed text, so the chat can open the instant an
 * answer is judged wrong — no waiting on the AI. The server's persisted copy
 * replaces this one as soon as adjudication returns.
 *
 * The server's version also names what it thinks went wrong („Atrodo, kad
 * apskaičiavai tikimybę…“), which this one cannot: the guess comes from the
 * adjudication call still in flight. Deliberately not awaited — the diagnosis
 * arriving a second later is worth far less than the chat opening now.
 */
/**
 * Mirrors `DiscussionGate::MIN_TURNS` (app/services/discussion_gate.rb) — the
 * learner turns about a wrong answer that satisfy the forced-resolution rule,
 * short of the tutor confirming the child got there.
 */
export const MIN_DISCUSSION_TURNS = 2;

/**
 * Mirrors `DiscussionTutor::HELP_TO_TEACH_MESSAGE`. A help conversation that
 * turned into a wrong-answer one carries the child's help turns, and those do
 * not count towards the wrong answer — the gate counts from this message on.
 */
const HELP_TO_TEACH_MESSAGE =
  "Šį kartą atsakymas dar ne tas — bet mes jau pradėjome kartu, tad tęsiame nuo tos\n" +
  "pačios vietos. Parašyk, kaip skaičiavai, ir rasime, kurioje vietoje nuklydo.";

export const DISCUSSION_OPENING_MESSAGE =
  "Dar ne visai — pažiūrėkime kartu. Perskaityk užduotį dar kartą ir pabandyk atsakyti antrą kartą. " +
  "Šį kartą kartu parašyk, kodėl manai, kad tavo atsakymas teisingas — vien atsakymo " +
  "neužteks, svarbiausia tavo mintis. Jei nori, kad paaiškinčiau, paspausk „Paaiškink man“ " +
  "— tada antrą kartą atsakinėti nebereikės.";

/**
 * Mirrors `DiscussionTutor::HELP_OPENING_MESSAGE`. Shown the instant the student
 * presses "Noriu suprasti", so asking for help never costs a wait.
 */
export const HELP_OPENING_MESSAGE =
  "Gerai, kad paklausei — kartu tikrai išsiaiškinsime. Pasakyk, ką jau supranti šioje " +
  "užduotyje arba nuo ko, tavo manymu, reikėtų pradėti. Jei visai neaišku, parašyk " +
  "„Nuo ko pradėti?“ — pradėsime nuo pirmo žingsnio.";

type UpgradedResult = "CORRECT" | "PARTIAL";

type Options = {
  sessionId?: string;
  /**
   * The AI accepted the answer, or a second attempt earned partial credit.
   * `viaChat` is false when the verdict came from background adjudication with
   * no conversation to read — nothing is left on screen to move the student on.
   */
  onResultChange: (exerciseId: string, result: UpgradedResult, viaChat: boolean) => void;
  /** AI unavailable — fall back to the old behaviour (hint/theory). */
  onDegraded: (exerciseId: string) => void;
  /**
   * The conversation ended (resolved or skipped). The branch comes with it
   * because the two ends are opposite: a DISCUSSION is over *after* the answer
   * and the next move is on to the next question, where a HELP conversation
   * ends *before* it — the child is going back to an answer field they have not
   * filled in yet, and it is sitting above a panel that has been holding the
   * screen for several turns.
   */
  onClosed?: (exerciseId: string, branch: Discussion["branch"]) => void;
};

/** A local, not-yet-persisted discussion so the panel can render immediately. */
function pendingDiscussion(): Discussion {
  return {
    id: "",
    status: "OPEN",
    branch: "DISCUSSION",
    stage: "RETRY",
    retryCorrect: false,
    solvedInChat: false,
    adjudication: null,
    messages: [
      { id: "local-opening", role: "ai", content: DISCUSSION_OPENING_MESSAGE, resolvedMarker: false },
    ],
  };
}

/** The same, for a student who asked for help before answering. */
function pendingHelpDiscussion(): Discussion {
  return {
    id: "",
    status: "OPEN",
    branch: "HELP",
    stage: "TEACH",
    retryCorrect: false,
    solvedInChat: false,
    adjudication: null,
    messages: [
      { id: "local-help-opening", role: "ai", content: HELP_OPENING_MESSAGE, resolvedMarker: false },
    ],
  };
}

function studentMessage(content: string, index: number) {
  return { id: `tmp-${index}`, role: "student" as const, content, resolvedMarker: false };
}

export function useDiscussions({ sessionId, onResultChange, onDegraded, onClosed }: Options) {
  const [adjudicateAnswer] = useMutation(ADJUDICATE_ANSWER);
  const [openAnswerDiscussion] = useMutation(OPEN_ANSWER_DISCUSSION);
  const [requestProblemHelp] = useMutation(REQUEST_PROBLEM_HELP);
  const [sendDiscussionMessage] = useMutation(SEND_DISCUSSION_MESSAGE);
  const [resolveDiscussionMut] = useMutation(RESOLVE_DISCUSSION);

  const [discussions, setDiscussions] = useState<Record<string, Discussion>>({});
  const [adjudicatingIds, setAdjudicatingIds] = useState<Set<string>>(new Set());
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());

  // A ref mirror keeps callbacks free of stale-closure reads.
  const ref = useRef<Record<string, Discussion>>({});
  // Answers typed before the server discussion exists; sent once it does.
  const queued = useRef<Map<string, string>>(new Map());

  const write = useCallback((next: Record<string, Discussion>) => {
    ref.current = next;
    setDiscussions(next);
  }, []);

  const update = useCallback(
    (fn: (prev: Record<string, Discussion>) => Record<string, Discussion>) => write(fn(ref.current)),
    [write]
  );

  const flag = (setter: typeof setSendingIds, exerciseId: string, on: boolean) =>
    setter((prev) => {
      const next = new Set(prev);
      if (on) next.add(exerciseId);
      else next.delete(exerciseId);
      return next;
    });

  const drop = useCallback(
    (exerciseId: string) => {
      queued.current.delete(exerciseId);
      flag(setSendingIds, exerciseId, false);
      update((prev) => {
        const next = { ...prev };
        delete next[exerciseId];
        return next;
      });
    },
    [update]
  );

  /** Restore persisted discussions from a loaded session. */
  const restore = useCallback(
    (exercises: Exercise[]) => {
      const restored: Record<string, Discussion> = {};
      for (const exercise of exercises) {
        if (exercise.discussion) restored[exercise.id] = exercise.discussion;
      }
      if (Object.keys(restored).length > 0) write(restored);
    },
    [write]
  );

  const deliver = useCallback(
    async (exerciseId: string, discussionId: string, message: string) => {
      flag(setSendingIds, exerciseId, true);
      try {
        const { data } = await sendDiscussionMessage({ variables: { discussionId, message } });
        const payload = (data as any)?.sendDiscussionMessage;
        const discussion = payload?.discussion as Discussion | undefined;
        if (discussion) {
          update((prev) => ({ ...prev, [exerciseId]: discussion }));
          const upgraded = payload?.problemResult as string | undefined;
          if (upgraded === "CORRECT" || upgraded === "PARTIAL") {
            onResultChange(exerciseId, upgraded, true);
            if (discussion.solvedInChat) {
              toast.success(
                discussion.retryCorrect
                  ? "Teisingai iš antro karto!"
                  : "Teisingai! Užduotis įvertinta „iš dalies“."
              );
            }
          }
          if (discussion.status !== "OPEN") onClosed?.(exerciseId, discussion.branch);
        }
      } catch {
        toast.error("Nepavyko išsiųsti žinutės");
      } finally {
        flag(setSendingIds, exerciseId, false);
      }
    },
    [sendDiscussionMessage, update, onResultChange, onClosed]
  );

  /**
   * Opens the chat immediately, then asks the AI to adjudicate in the
   * background: it may accept the answer (chat closes) or confirm the miss
   * (the local placeholder is swapped for the persisted conversation).
   *
   * Both calls go out together. Adjudication is the slow one — a provider call
   * with a tail of tens of seconds — and it used to be the only thing that could
   * produce a discussion id, so a child who read the opening line and typed
   * straight away sat watching their own message spin until it returned. On this
   * instance 37% of conversations were in exactly that state. `openAnswerDiscussion`
   * makes the id available in one cheap round trip, so the message goes to the
   * tutor while the adjudicator is still thinking.
   */
  const start = useCallback(
    async (exerciseId: string, answer: string) => {
      update((prev) => ({ ...prev, [exerciseId]: pendingDiscussion() }));
      flag(setAdjudicatingIds, exerciseId, true);

      // Not awaited: it only needs to land before the child finishes typing, and
      // waiting on it here would rebuild the very serial chain being removed. A
      // failure is silent by design — `current.id` simply stays empty and the
      // message queues exactly as it did before this existed.
      void openAnswerDiscussion({ variables: { sessionId, exerciseId, answer } })
        .then(({ data }) => {
          const opened = (data as any)?.openAnswerDiscussion?.discussion as Discussion | undefined;
          if (!opened) return;
          const current = ref.current[exerciseId];
          // Gone (degraded, or the answer was accepted) or already replaced by
          // the adjudicated copy — either way this is stale news.
          if (!current || current.id) return;
          update((prev) => ({
            ...prev,
            // The server's opening turn, but the child's own typed messages:
            // they are on screen and may already be queued for sending.
            [exerciseId]: { ...opened, messages: [...opened.messages, ...current.messages.slice(1)] },
          }));
          const waiting = queued.current.get(exerciseId);
          if (waiting) {
            queued.current.delete(exerciseId);
            void deliver(exerciseId, opened.id, waiting);
          }
        })
        .catch(() => {});

      try {
        const { data } = await adjudicateAnswer({
          variables: { sessionId, exerciseId, answer },
        });
        const result = (data as any)?.adjudicateAnswer;

        if (!result || result.degraded) {
          drop(exerciseId);
          onDegraded(exerciseId);
          return;
        }

        if (result.branch === "REWRITE" && (result.result === "CORRECT" || result.result === "PARTIAL")) {
          drop(exerciseId);
          onResultChange(exerciseId, result.result, false);
          toast.success(
            result.exerciseRewritten
              ? "Užduotis buvo netiksli — pataisyta, tavo atsakymas priimtas!"
              : "Tavo atsakymas priimtas!"
          );
          return;
        }

        if (result.branch === "DISCUSSION" && result.discussion) {
          const server = result.discussion as Discussion;
          const waiting = queued.current.get(exerciseId);
          queued.current.delete(exerciseId);
          update((prev) => {
            const local = prev[exerciseId];
            // The conversation may have moved on while the adjudicator was
            // thinking — that is the entire point of opening the row early — and
            // this payload was serialized before those turns existed. Replacing
            // wholesale would wipe the tutor's reply off the screen. So the
            // longer transcript wins, and only the adjudicator's own findings
            // are merged in.
            if (local && local.messages.length > server.messages.length) {
              return { ...prev, [exerciseId]: { ...local, adjudication: server.adjudication } };
            }
            return {
              ...prev,
              // Keep the student's already-typed answer on screen while it is sent.
              [exerciseId]: waiting
                ? { ...server, messages: [...server.messages, studentMessage(waiting, server.messages.length)] }
                : server,
            };
          });
          if (waiting) void deliver(exerciseId, server.id, waiting);
          return;
        }

        drop(exerciseId);
        onDegraded(exerciseId);
      } catch {
        drop(exerciseId);
        onDegraded(exerciseId);
      } finally {
        flag(setAdjudicatingIds, exerciseId, false);
      }
    },
    [adjudicateAnswer, sessionId, update, drop, onDegraded, onResultChange, deliver]
  );

  /**
   * "Noriu suprasti" on a task the student has not answered. Opens the same chat
   * the tutor uses after a wrong answer, but in teaching mode: nothing is graded
   * here, and the answer field behind the panel stays open the whole time.
   */
  const requestHelp = useCallback(
    async (exerciseId: string) => {
      if (ref.current[exerciseId]) return;
      update((prev) => ({ ...prev, [exerciseId]: pendingHelpDiscussion() }));

      try {
        const { data } = await requestProblemHelp({ variables: { sessionId, exerciseId } });
        const discussion = (data as any)?.requestProblemHelp?.discussion as Discussion | undefined;
        if (!discussion) {
          drop(exerciseId);
          onDegraded(exerciseId);
          return;
        }
        // Anything typed into the placeholder while the request was in flight.
        const waiting = queued.current.get(exerciseId);
        queued.current.delete(exerciseId);
        update((prev) => ({
          ...prev,
          [exerciseId]: waiting
            ? { ...discussion, messages: [...discussion.messages, studentMessage(waiting, discussion.messages.length)] }
            : discussion,
        }));
        if (waiting) void deliver(exerciseId, discussion.id, waiting);
      } catch {
        drop(exerciseId);
        onDegraded(exerciseId);
      }
    },
    [requestProblemHelp, sessionId, update, drop, onDegraded, deliver]
  );

  const send = useCallback(
    (exerciseId: string, message: string) => {
      const current = ref.current[exerciseId];
      if (!current) return;

      // Show it right away, whether or not the server discussion exists yet.
      update((prev) => ({
        ...prev,
        [exerciseId]: {
          ...current,
          messages: [...current.messages, studentMessage(message, current.messages.length)],
        },
      }));

      if (!current.id) {
        // Still adjudicating — hold it, and show it as in-flight until sent.
        queued.current.set(exerciseId, message);
        flag(setSendingIds, exerciseId, true);
        return;
      }
      void deliver(exerciseId, current.id, message);
    },
    [update, deliver]
  );

  const resolve = useCallback(
    async (exerciseId: string, skipped: boolean) => {
      const current = ref.current[exerciseId];
      if (!current) return;
      // Read off the conversation being closed, not off the reply: the panel is
      // dropped on the failure paths below, and the server copy may not arrive
      // at all.
      const branch = current.branch;
      if (!current.id) {
        // Nothing persisted yet — just close the local panel.
        drop(exerciseId);
        onClosed?.(exerciseId, branch);
        return;
      }
      try {
        const { data } = await resolveDiscussionMut({
          variables: { discussionId: current.id, skipped },
        });
        const discussion = (data as any)?.resolveDiscussion?.discussion as Discussion | undefined;
        if (discussion) update((prev) => ({ ...prev, [exerciseId]: discussion }));
      } catch {
        /* ignore — never trap the student behind a failed request */
      }
      onClosed?.(exerciseId, branch);
    },
    [resolveDiscussionMut, update, drop, onClosed]
  );

  const isAdjudicating = useCallback((exerciseId: string) => adjudicatingIds.has(exerciseId), [adjudicatingIds]);
  const isSending = useCallback((exerciseId: string) => sendingIds.has(exerciseId), [sendingIds]);

  /**
   * True while an exercise still has an open conversation to finish.
   *
   * A help conversation is not one of those. It owes the app nothing — nothing
   * in it is graded, and the student opened it voluntarily — so leaving it on
   * screen must not disable "hand in" behind a tooltip telling them to go
   * discuss a task they have already answered.
   */
  const blocks = useCallback(
    (exercise?: Exercise) => {
      if (!exercise) return false;
      if (adjudicatingIds.has(exercise.id)) return true;
      const discussion = discussions[exercise.id];
      return discussion?.status === "OPEN" && discussion.branch !== "HELP";
    },
    [adjudicatingIds, discussions]
  );

  /**
   * True while a question still owes the tutor a conversation, under the
   * optional rule a grown-up switches on per learner.
   *
   * `DiscussionGate` on the server is the authority and refuses `completeSession`
   * on the same reading — this is here so the screen can say which questions and
   * why, and so it keeps up as the child chats without a refetch.
   *
   * The rule: the tutor confirmed the child got there, or the child replied
   * `MIN_DISCUSSION_TURNS` times about this wrong answer — the second attempt
   * and at least one reaction to the teaching that follows it. Not "the stage
   * reached TEACH": that happens on the very first reply, so the gate opened
   * exactly when the explanation arrived. The turn count also works when the
   * tutor is down, since a child's turn is saved before the AI is called.
   *
   * Deliberately not "the chat is closed": that is one tap, and it would make
   * the whole rule decorative. Nor "solved in the chat" alone: a child who
   * cannot get there would never be let out.
   */
  const owesDiscussion = useCallback(
    (exercise?: Exercise) => {
      if (!exercise) return false;
      // Reported as broken: set aside and not graded, so there is no wrong
      // answer left to talk through — DiscussionGate says the same.
      if (exercise.reportedAt) return false;
      const discussion = discussions[exercise.id];
      // No conversation is not an unfinished one — a wrong answer the provider
      // never opened a chat about cannot be required to have finished one.
      if (!discussion || discussion.branch === "HELP") return false;
      if (discussion.solvedInChat) return false;

      const resumedAt = discussion.messages.findLastIndex(
        (message) => message.role === "ai" && message.content === HELP_TO_TEACH_MESSAGE
      );
      const turns = discussion.messages
        .slice(resumedAt + 1)
        .filter((message) => message.role === "student").length;
      return turns < MIN_DISCUSSION_TURNS;
    },
    [discussions]
  );

  const anyOpen = Object.values(discussions).some((discussion) => discussion.status === "OPEN");

  return {
    discussions,
    restore,
    start,
    requestHelp,
    send,
    resolve,
    isAdjudicating,
    isSending,
    blocks,
    owesDiscussion,
    anyOpen,
  };
}
